import { useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './style.less';

const NORMAL_SPEED = 1;
const SLOW_SPEED = 0.15;

export const ProjectMarquee = ({ projects }) => {
  const trackRef = useRef(null);
  const animationRef = useRef(null);

  // Initialize Web Animations API for smooth speed control
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    // Respect reduced motion preferences
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return;

    // Create animation using Web Animations API
    const animation = track.animate(
      [{ transform: 'translateX(0)' }, { transform: 'translateX(-50%)' }],
      {
        duration: 60000,
        iterations: Infinity,
        easing: 'linear',
      }
    );

    animationRef.current = animation;

    return () => {
      animation.cancel();
    };
  }, []);

  const handleMouseEnter = () => {
    if (animationRef.current) {
      animationRef.current.playbackRate = SLOW_SPEED;
    }
  };

  const handleMouseLeave = () => {
    if (animationRef.current) {
      animationRef.current.playbackRate = NORMAL_SPEED;
    }
  };

  // Validate projects prop
  if (!projects || !Array.isArray(projects) || projects.length === 0) {
    return null;
  }

  // Filter valid projects with required fields
  const validProjects = projects.filter((p) => p.id && p.image && p.title);
  if (validProjects.length === 0) {
    return null;
  }

  // Duplicate projects to ensure seamless loop
  const duplicatedProjects = [...validProjects, ...validProjects];

  return (
    <div
      className="project-marquee"
      role="region"
      aria-label="Project gallery carousel"
      tabIndex={0}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleMouseEnter}
      onBlur={handleMouseLeave}
    >
      <div ref={trackRef} className="marquee-track" aria-live="off">
        {duplicatedProjects.map((project, index) => (
          <Link
            key={`${project.id}-${index}`}
            to={`/projects#${project.id}`}
            className="marquee-item"
            tabIndex={index >= validProjects.length ? -1 : 0}
            aria-label={`View ${project.title} project`}
          >
            <div className="marquee-image">
              <img
                src={project.image}
                alt={project.title}
                loading={index < 3 ? 'eager' : 'lazy'}
              />
              <div className="marquee-overlay">
                <span className="marquee-title">{project.title}</span>
                <span className="marquee-style">{project.style}</span>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};
